import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { label: "Services", href: "/#services" },
  { label: "Why SWIA", href: "/#why" },
  { label: "Process", href: "/#process" },
  { label: "Contact", href: "/#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const onNavClick = (e, href) => {
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
      } else {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-neutral-200"
          : "bg-white/40 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <Logo size="md" />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.href}
              onClick={(e) => onNavClick(e, n.href)}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-[13px] tracking-[0.08em] uppercase text-neutral-600 hover:text-black transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/quote"
            data-testid="nav-get-quote-btn"
            className="hidden md:inline-flex items-center gap-2 bg-black text-white px-5 py-2.5 rounded-full text-[13px] font-medium tracking-wide hover:scale-[1.02] active:scale-[0.98] transition-transform"
          >
            Get a Quote
            <span aria-hidden>→</span>
          </Link>

          <button
            type="button"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 text-black"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="md:hidden border-t border-neutral-200 bg-white"
          data-testid="nav-mobile-panel"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={(e) => onNavClick(e, n.href)}
                data-testid={`nav-link-mobile-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-base tracking-wide text-neutral-700"
              >
                {n.label}
              </a>
            ))}
            <Link
              to="/quote"
              data-testid="nav-get-quote-mobile-btn"
              className="mt-2 inline-flex w-max items-center gap-2 bg-black text-white px-5 py-3 rounded-full text-sm font-medium"
            >
              Get a Quote →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
